import React, { useMemo } from "react";
import { useGetTransportTypesList } from "../../../../../api/static-data";
import { FormInput } from "../../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { InternalTransportRecord } from "../../../../../types/prodcut-data-review/product-data.types";
import { DEFAULT_FORM_VALUES } from "../constants/internalTransportTable.constants";
import { WEIGHT_UNIT_OPTIONS } from "../constants/transportTable.constants";
import { InternalTransportFormValues } from "../types/internalTransportTable.types";
import { fromFormValues, toFormValues } from "../utils/internalTransportTable.utils";
import EditableTable, { ColumnDef } from "../../shared/components/EditableTable";

// ── Props ────────────────────────────────────────────────────────────────────
interface InternalTransportTableProps {
  records: InternalTransportRecord[];
  onSave: (id: string, patch: Partial<InternalTransportRecord>) => void;
  onEdit?: (id: string) => void;
  onCancel?: (id: string) => void;
  disabled?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────
const InternalTransportTable: React.FC<InternalTransportTableProps> = ({
  records,
  onSave,
  onEdit,
  onCancel,
  disabled = false
}) => {
  const { data: transportTypes } = useGetTransportTypesList();

  const transportTypeOptions = useMemo(
    () => (transportTypes ?? []).map((t: any) => ({ value: t.text as string, label: t.text as string })),
    [transportTypes]
  );

  const columns = useMemo<ColumnDef<InternalTransportRecord, InternalTransportFormValues>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        cell: r => <span className="tw-text-sm">{r.name || "—"}</span>,
        editCell: ({ control }) => (
          <FormInput control={control} name="name" label="" className="tw-h-8 tw-text-xs" />
        )
      },
      {
        id: "averageDistance",
        header: "Avg. Distance (km)",
        cell: r => <span className="tw-text-sm tw-font-mono">{r.averageDistance || "—"}</span>,
        editCell: ({ control }) => (
          <FormInputNumber
            control={control}
            name="averageDistance"
            label=""
            className="tw-h-8 tw-text-xs tw-font-mono"
          />
        )
      },
      {
        id: "transportType",
        header: "Transport Type",
        cell: r => <span className="tw-text-sm">{r.transportType || "—"}</span>,
        editCell: ({ control }) => (
          <FormSelect
            control={control}
            name="transportType"
            label=""
            placeholder="Select"
            options={transportTypeOptions}
          />
        )
      },
      {
        id: "weight",
        header: "Weight",
        cell: r => (
          <span className="tw-text-sm tw-font-mono">
            {r.weight !== "" ? `${r.weight} ${r.weightUnit}` : "—"}
          </span>
        ),
        editCell: ({ control }) => (
          <FormUnitInput
            control={control}
            name="weight"
            label=""
            unitName="weightUnit"
            unitOptions={WEIGHT_UNIT_OPTIONS}
          />
        )
      }
    ],
    [transportTypeOptions]
  );

  return (
    <EditableTable
      records={records}
      columns={columns}
      defaultFormValues={DEFAULT_FORM_VALUES}
      toFormValues={toFormValues}
      fromFormValues={fromFormValues}
      onSave={onSave}
      onEdit={onEdit}
      onCancel={onCancel}
      emptyLabel="internal transports"
      disabled={disabled}
    />
  );
};

export default InternalTransportTable;
